import { inject, Injectable } from '@angular/core';
import { MatSnackBar, MatSnackBarConfig } from '@angular/material/snack-bar';
import { IToast, ToastType } from '../model/toast.model';
import { SnackbarComponent } from '../snackbar.component';

@Injectable({ providedIn: 'root' })
export class SnackbarService {
  _toasts: IToast[] = [];

  private defaultConfig: MatSnackBarConfig = {
    duration: 5000, // 5 secondi
    horizontalPosition: 'right',
    verticalPosition: 'top',
  };
  /**
   * Reference to the snackbar.
   */
  private _snackBar = inject(MatSnackBar);

  /**
   * Show success snackbar.
   */
  showSuccessSnackbar(message: string, header?: string): void {
    const config: MatSnackBarConfig = {
      ...this.defaultConfig,
      panelClass: ['success-snackbar'],
    };
    const ref = this._snackBar.openFromComponent(SnackbarComponent, {
      ...config,
    });

    ref.instance.type.set(ToastType.SUCCESS);
    ref.instance.header.set(header);
    ref.instance.message.set(message);
  }

  /**
   * Show warning snackbar.
   */
  showWarningSnackbar(message: string, header?: string): void {
    const config: MatSnackBarConfig = {
      ...this.defaultConfig,
      panelClass: ['warning-snackbar'],
    };
    const ref = this._snackBar.openFromComponent(SnackbarComponent, {
      ...config,
    });

    ref.instance.type.set(ToastType.WARNING);
    ref.instance.header.set(header);
    ref.instance.message.set(message);
  }

  /**
   * Show warning snackbar with HTML support.
   */
  showWarningSnackbarHtml(htmlMessage: string, header?: string): void {
    const config: MatSnackBarConfig = {
      ...this.defaultConfig,
      duration: 8000, // Durata più lunga per messaggi HTML (liste di errori)
      panelClass: ['warning-snackbar'],
    };
    const ref = this._snackBar.openFromComponent(SnackbarComponent, {
      ...config,
    });

    ref.instance.type.set(ToastType.WARNING);
    ref.instance.header.set(header);
    ref.instance.message.set(htmlMessage);
  }

  /**
   * Show danger snackbar.
   */
  showDangerSnackbar(message: string): void {
    const config: MatSnackBarConfig = {
      ...this.defaultConfig,
      panelClass: ['danger-snackbar'],
    };
    const ref = this._snackBar.openFromComponent(SnackbarComponent, {
      ...config,
    });

    ref.instance.type.set(ToastType.DANGER);
    ref.instance.message.set(message);
  }

  /**
   * Show info snackbar.
   */
  showInfoSnackbar(message: string, header?: string): void {
    const config: MatSnackBarConfig = {
      ...this.defaultConfig,
      panelClass: ['info-snackbar'],
    };
    const ref = this._snackBar.openFromComponent(SnackbarComponent, {
      ...config,
    });

    ref.instance.type.set(ToastType.INFO);
    ref.instance.message.set(message);
    ref.instance.header.set(header);
  }

  /**
   * Show a snackbar with a custom action button.
   */
  showCustomActionSnackbar(
    message: string,
    actionLabel: string,
    actionCallback: () => void,
    type: ToastType = ToastType.INFO,
    duration = 0
  ): void {
    const config: MatSnackBarConfig = {
      ...this.defaultConfig,
      duration: duration,
      panelClass: ['info-snackbar'],
    };

    const ref = this._snackBar.openFromComponent(SnackbarComponent, {
      ...config,
    });

    ref.instance.type.set(type);
    ref.instance.message.set(message);
    ref.instance.actionLabel.set(actionLabel);
    ref.instance.actionCallback = actionCallback;
  }
}
